import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Calendar, Users, Move } from "lucide-react";
import Navbar from "../../components/Navbar";

import dance1 from "../../data/obory/tanecni/DSC_4000b.jpg";
import dance2 from "../../data/obory/tanecni/Galavecer-147a.jpg";
import dance3 from "../../data/obory/tanecni/Galavecer-217a.jpg";
import dance4 from "../../data/obory/tanecni/Galavecer-315a.jpg";
import dance5 from "../../data/obory/tanecni/Galavecer-80a.jpg";
import dance6 from "../../data/obory/tanecni/Soutez1.jpg";
import dance7 from "../../data/obory/tanecni/Soutez2.jpg";

// Color variables (same as Vytvarni)
const primaryColor = "#96522b";
const primaryColorLight = "#b07a56";
const primaryColorLighter = "#ca9e81";
const textOnPrimary = "#ffffff";

interface SliderImage {
  src: string;
  alt: string;
  link: string;
}

const sliderImages: SliderImage[] = [
  {
    src: dance1,
    alt: "Taneční vystoupení žáků",
    link: "/galerie/video"
  },
  {
    src: dance2,
    alt: "Taneční vystoupení žáků",
    link: "/galerie/video"
  },
  {
    src: dance3,
    alt: "Taneční vystoupení žáků",
    link: "/galerie/video"
  },
  {
    src: dance4,
    alt: "Taneční vystoupení žáků",
    link: "/galerie/video"
  },
  {
    src: dance5,
    alt: "Taneční vystoupení žáků",
    link: "/galerie/video"
  },
  {
    src: dance6,
    alt: "Taneční vystoupení žáků",
    link: "/galerie/video"
  },
  {
    src: dance7,
    alt: "Taneční vystoupení žáků",
    link: "/galerie/video"
  }
];

const Tanecni: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === sliderImages.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? sliderImages.length - 1 : prev - 1));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const danceStyles = [
    { name: "Klasický tanec", icon: "🩰" },
    { name: "Moderní tanec", icon: "💃" },
    { name: "Contemporary", icon: "🧘" },
    { name: "Lidový tanec", icon: "👯" },
    { name: "Street dance", icon: "🕺" },
    { name: "Show dance", icon: "✨" }
  ];

  return (
    <div className="min-h-screen bg-[rgba(150,82,43,0.1)] pt-[80px]">
      {/* Navbar */}
      <Navbar />
      
      {/* Full-width Hero Slider */}
      <div className="relative w-screen -ml-[calc(50vw-50%)] h-[90vh]">
        {sliderImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src={image.src}
              alt={image.alt}
              className="w-full h-full object-cover"
            />
            <Link to={image.link}>
              <div 
                className="absolute inset-0 flex items-center justify-center"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              >
                <h2 className="text-5xl md:text-7xl font-bold text-white text-center">
                  Taneční obor
                </h2>
              </div>
            </Link>
          </div>
        ))}
        
        {/* Navigation arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-2 rounded-full transition-all"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-2 rounded-full transition-all"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
        
        {/* Dots indicator */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
          {sliderImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-white w-6' : 'bg-white/50'}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Main content container */}
      <div className="container mx-auto px-4 sm:px-6 pt-16 pb-12">
        {/* About section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="p-6 md:p-10">
            <div className="flex items-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800">O tanečním oboru</h2>
            </div>
            <div className="space-y-4 md:space-y-6 text-base md:text-lg text-gray-700">
              <p className="leading-relaxed">
                Taneční obor naší školy rozvíjí pohybové nadání dětí prostřednictvím různých tanečních stylů.
                Žáci se učí základům klasického, moderního i lidového tance.
              </p>
              <p className="leading-relaxed">
                Nejmenší žáci v přípravném studiu poznávají první taneční krůčky formou gymnastického cvičení a rytmických her.
                Starší žáci získávají přehled v různých tanečních technikách.
              </p>
              <p className="leading-relaxed">
                Kromě praktické výuky je součástí i teorie tance, která se zaměřuje na dějiny tance a poznávání různých tanečních stylů.
              </p>
            </div>
          </div>
        </div>

        {/* Dance Styles section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="p-6 md:p-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Taneční styly</h2>
            <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
              Náš taneční obor nabízí rozmanité taneční techniky a přístupy
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {danceStyles.map((style) => (
                <div 
                  key={style.name} 
                  className="flex items-start p-3 md:p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all border border-gray-200 hover:border-gray-300 group"
                >
                  <span className="text-2xl md:text-3xl mr-3 md:mr-4" style={{ color: primaryColor }}>
                    {style.icon}
                  </span>
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-gray-800 group-hover:text-amber-900 transition-colors">
                      {style.name}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div 
          className="rounded-xl shadow-lg overflow-hidden"
          style={{ backgroundColor: primaryColor }}
        >
          <div className="p-6 md:p-12 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Máte zájem o studium tanečního oboru?
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
              <Link
                to="/chcinazus/prijmacirizeni"
                className="px-6 md:px-8 py-2 md:py-3 bg-white font-medium rounded-lg transition-all transform hover:scale-105"
                style={{ color: primaryColor }}
              >
                Přihláška ke studiu
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tanecni;